import React from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import IFormStep from 'interfaces/IFormStep';
import { LoadingError } from 'components/LoadingError';
import _ from 'lodash';
import IClinicalContact from '../../../../interfaces/IClinicalContact';
import HelpButton from '../../../Form/HelpButton';
import AgeMapping from '../AgeMapping';
import ClinicalContactActions from './ClinicalContactActions';
import ClinicalContactModal from './ClinicalContactModal';

const defaultEntry = {
    supervisor: { id: '', name: '' },
};

type ClientSortField = 'startDate';

const ClinicalContact: React.FC<IFormStep> = ({
    formData,
    requirements,
    handleInputChange,
    models,
}) => {
    const numberOfClientRequired = formData.experiences.find((e) => e.name === 'clients-treated')
        ?.value as number;
    const clinicalContacts = formData.clinicalContacts ?? [];
    const sortField: ClientSortField = 'startDate';

    const generateNewClientTemplate = (template?: IClinicalContact): IClinicalContact => {
        const defaultAge = !requirements.config?.isCA ? 'C0' : undefined;
        if (template) {
            const { supervisor, treatmentSetting } = template;
            return {
                supervisor,
                treatmentSetting,
                age: defaultAge,
            };
        }

        if (!clinicalContacts?.length) {
            return defaultEntry;
        }

        const { supervisor, treatmentSetting } = clinicalContacts[clinicalContacts.length - 1];

        return {
            supervisor,
            treatmentSetting,
            age: defaultAge,
        };
    };

    const [editableClinicalContact, setEditableClinicalContact] = React.useState<IClinicalContact>(
        generateNewClientTemplate()
    );
    const [keepModalOpen, setKeepModalOpen] = React.useState(false);

    const handleEditableClinicalContactUpdate = (name: string, value: any) => {
        setEditableClinicalContact((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleClinicalContactsChange = (value: IClinicalContact[]) =>
        handleInputChange('clinicalContacts', sortClinicalContacts(value));

    const handleUpdateClinicalContact = (index: number) => {
        const updatedClinicalContacts = clinicalContacts.map((cc, i) => {
            if (i === index) {
                return editableClinicalContact;
            }

            return cc;
        });
        handleClinicalContactsChange(updatedClinicalContacts);
        setEditableClinicalContact(generateNewClientTemplate(editableClinicalContact));
    };

    const handleAddNewClinicalContact = () => {
        handleClinicalContactsChange(clinicalContacts.concat([editableClinicalContact]));
        setEditableClinicalContact(generateNewClientTemplate(editableClinicalContact));
    };

    const handleDeleteClient = (index: number) => {
        handleClinicalContactsChange(_.filter(clinicalContacts, (_item, i) => i !== index));
        setEditableClinicalContact(generateNewClientTemplate());
    };

    const clientRequirements = requirements.experience?.find((e) => e.name === 'clients-treated');
    if (!clientRequirements) return <LoadingError />;

    const help = requirements.config?.help && requirements.config?.help['clinicalContacts'];

    const isValid = (contact: IClinicalContact) => {
        return models.formValidator.validateClinicalContact(contact);
    };

    const sortClinicalContacts = (clinicalContacts: IClinicalContact[]) => {
        return _.sortBy(clinicalContacts, (cc) => {
            return cc[sortField];
        });
    };

    const isCA: boolean = (requirements.config as any).isCA || false;

    const toNiceAge = (age?: string) => AgeMapping[age || ''] ?? '18+';

    const showAge = isCA || clinicalContacts.some((c) => !!c.age);

    return (
        <>
            <Row>
                <h3 className="fw-bold mb-3">
                    EMDR clinical contact activity{' '}
                    {help && <HelpButton title={'Clinical contacts'} content={help} />}
                </h3>
            </Row>
            <Row className={'mb-3 col-10 text-black fw-normal'}>
                <h4 className={'fw-normal'}>
                    Add details for each client separately. A new record entry form will appear
                    automatically when you complete a record.
                </h4>
                <h4 className={'fw-normal'}>
                    You'll need to enter at least {clientRequirements.min}{' '}
                    {clientRequirements.max ? ` (max ${clientRequirements.max}) ` : ''}
                    different cases to continue your application. The majority of records you enter
                    should reflect all 8 phases.
                </h4>
            </Row>
            {clinicalContacts.length > 0 && (
                <Row className={'mb-4'}>
                    <Col xs={12}>
                        <Table responsive striped className={'client-contact-table'}>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Client initials</th>
                                    {showAge && <th>Age</th>}
                                    <th>EMDR start date</th>
                                    <th>Presenting problem</th>
                                    <th>Sessions</th>
                                    <th>Treatment setting</th>
                                    <th>Supervisor</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {sortClinicalContacts(clinicalContacts).map((contact, index) => (
                                    <tr key={'clinicalContact-' + index}>
                                        <td>{index + 1}</td>
                                        <td>{contact.initials}</td>
                                        {showAge && <td>{toNiceAge(contact.age)}</td>}
                                        <td>{new Date(contact.startDate!).toLocaleDateString()}</td>
                                        <td>{contact.presentingProblem}</td>
                                        <td>{contact.numberOfSessions}</td>
                                        <td>{contact.treatmentSetting}</td>
                                        <td>{contact.supervisor?.name}</td>
                                        <td>
                                            <ClinicalContactActions
                                                handleUpdateClinicalContact={
                                                    handleUpdateClinicalContact
                                                }
                                                index={index}
                                                setEditableClinicalContact={
                                                    setEditableClinicalContact
                                                }
                                                generateNewClientTemplate={
                                                    generateNewClientTemplate
                                                }
                                                contact={contact}
                                                isValid={isValid}
                                                editableClinicalContact={editableClinicalContact}
                                                isCA={isCA}
                                                handleEditableClinicalContactUpdate={
                                                    handleEditableClinicalContactUpdate
                                                }
                                                handleDeleteClient={handleDeleteClient}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            )}
            {clinicalContacts?.length < numberOfClientRequired && (
                <Row>
                    <Col xs={5}>
                        <ClinicalContactModal
                            keepModalOpen={keepModalOpen}
                            setKeepModalOpen={setKeepModalOpen}
                            editableClinicalContact={editableClinicalContact}
                            isCA={isCA}
                            isValid={isValid}
                            handleAddNewClinicalContact={handleAddNewClinicalContact}
                            handleEditableClinicalContactUpdate={
                                handleEditableClinicalContactUpdate
                            }
                            setEditableClinicalContact={setEditableClinicalContact}
                            generateNewClientTemplate={generateNewClientTemplate}
                        />
                    </Col>
                </Row>
            )}
            {/*{clientInfoCard}*/}
        </>
    );
};

export default ClinicalContact;
